import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { api, formatUSD, formatApiErrorDetail } from "@/lib/api";
import { ArrowUpRight, ArrowCounterClockwise, Star, ImageSquare, Archive as ArchiveIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

const SERVICE_LABEL = {
  flyer: "Flyer", logo: "Logo", seal: "Seal", banner: "Banner", web: "Web", other: "Other",
};

export default function AdminArchive() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/projects?archived=true");
      // Sort: most recently archived first (fall back to created_at)
      const sorted = [...data].sort((a, b) => {
        const aDate = a.archived_at || a.created_at || "";
        const bDate = b.archived_at || b.created_at || "";
        return bDate.localeCompare(aDate);
      });
      setProjects(sorted);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load archive");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const unarchive = async (p) => {
    if (!window.confirm(`Restore "${p.title}" to the active project list?`)) return;
    try {
      await api.patch(`/projects/${p.id}/archive`, { archived: false });
      toast.success("Restored to active projects");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const requestReview = async (p) => {
    if (!window.confirm(`Email ${p.client_email} a review request for "${p.title}"?`)) return;
    try {
      await api.post("/admin/reviews/request", { client_id: p.client_id, project_id: p.id });
      toast.success(`Review request emailed to ${p.client_email}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send");
    }
  };

  const featureInPortfolio = async (p) => {
    if (!window.confirm(`Add "${p.title}" to your public portfolio (as draft)?`)) return;
    try {
      await api.post("/admin/portfolio", {
        title: p.title,
        description: p.description || "",
        service_type: p.service_type || "other",
        project_id: p.id,
        client_name: p.client_name || null,
        is_published: false,
      });
      toast.success("Added to portfolio (draft) — go to Portfolio to upload a cover");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const visible = filter
    ? projects.filter((p) => {
        const q = filter.toLowerCase();
        return (
          p.title?.toLowerCase().includes(q) ||
          p.client_name?.toLowerCase().includes(q) ||
          p.client_email?.toLowerCase().includes(q)
        );
      })
    : projects;

  return (
    <AdminLayout>
      <div className="mb-8 flex flex-col gap-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Archive</div>
            <h1 className="font-display text-5xl mt-2">Completed projects</h1>
            <p className="text-sm text-[#a3a3a3] mt-2 max-w-2xl">
              Projects you've archived live here — out of your active board but still available for
              review requests and portfolio publishing.
            </p>
          </div>
          <Link
            to="/admin/projects"
            data-testid="back-to-projects"
            className="btn-outline px-4 py-2.5 text-xs uppercase tracking-wider flex items-center gap-2 self-start"
          >
            <ArrowCounterClockwise size={12} /> Back to active board
          </Link>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <input
            type="text"
            placeholder="Search by project, client, or email…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="archive-filter"
            className="flex-1 min-w-[240px] bg-[#0a0a0a] border border-[#262626] px-4 py-2 text-sm text-white focus:border-[#FF2D2D] outline-none"
          />
          <div className="text-xs text-[#a3a3a3] tabular-nums">
            {visible.length} archived
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-[#a3a3a3] text-sm">Loading archive…</div>
      ) : visible.length === 0 ? (
        <div className="border border-[#262626] bg-[#0a0a0a] p-10 text-center" data-testid="archive-empty">
          <ArchiveIcon size={32} className="text-[#262626] mx-auto" />
          <div className="font-display text-2xl mt-4">No archived projects</div>
          <div className="text-sm text-[#a3a3a3] mt-2">
            Archive projects from the main board once they're complete. They'll appear here, out of
            your active workload but still actionable.
          </div>
          <Link
            to="/admin/projects"
            className="btn-outline inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider mt-5"
          >
            Go to active board <ArrowUpRight size={12} />
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-px bg-[#262626] border border-[#262626]" data-testid="archive-list">
          {visible.map((p) => (
            <div
              key={p.id}
              className="bg-[#0a0a0a] p-5 group hover:bg-[#0f0f0f] transition-colors"
              data-testid={`archived-project-${p.id}`}
            >
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
                <div className="lg:col-span-5 min-w-0">
                  <Link
                    to={`/admin/projects/${p.id}`}
                    className="font-semibold hover:text-[#FF2D2D] block truncate"
                    data-testid={`archived-title-${p.id}`}
                  >
                    {p.title}
                  </Link>
                  <div className="text-xs text-[#a3a3a3] mt-1 flex items-center gap-3 flex-wrap">
                    <span className="uppercase tracking-wider text-[10px]">
                      {SERVICE_LABEL[p.service_type] || p.service_type}
                    </span>
                    {p.status && (
                      <span className="capitalize">{p.status}</span>
                    )}
                    {p.payment_status && (
                      <span className="capitalize text-[#FF2D2D]">{p.payment_status.replace(/_/g, " ")}</span>
                    )}
                  </div>
                </div>
                <div className="lg:col-span-3 min-w-0">
                  <div className="text-sm truncate">{p.client_name || "—"}</div>
                  <div className="text-xs text-[#a3a3a3] truncate">{p.client_email}</div>
                </div>
                <div className="lg:col-span-2 text-sm tabular-nums">
                  {p.payment_method === "points" ? `${p.total_points} pts` : formatUSD(p.total_usd || 0)}
                  <div className="text-[10px] text-[#a3a3a3] uppercase tracking-wider mt-0.5">
                    {p.created_at ? new Date(p.created_at).toLocaleDateString() : ""}
                  </div>
                </div>
                <div className="lg:col-span-2 flex flex-wrap gap-1.5 justify-start lg:justify-end">
                  <button
                    onClick={() => requestReview(p)}
                    data-testid={`archive-request-review-${p.id}`}
                    title={p.last_review_request_at ? `Last requested ${new Date(p.last_review_request_at).toLocaleDateString()}` : "Email client a review request"}
                    className="p-2 border border-[#262626] text-[#a3a3a3] hover:text-[#FF2D2D] hover:border-[#FF2D2D]"
                  >
                    <Star size={13} weight={p.last_review_request_at ? "fill" : "regular"} className={p.last_review_request_at ? "text-[#FF2D2D]" : ""} />
                  </button>
                  <button
                    onClick={() => featureInPortfolio(p)}
                    data-testid={`archive-feature-${p.id}`}
                    title="Add to public portfolio (draft)"
                    className="p-2 border border-[#262626] text-[#a3a3a3] hover:text-[#FF2D2D] hover:border-[#FF2D2D]"
                  >
                    <ImageSquare size={13} />
                  </button>
                  <button
                    onClick={() => unarchive(p)}
                    data-testid={`archive-unarchive-${p.id}`}
                    title="Restore to active board"
                    className="p-2 border border-[#262626] text-[#a3a3a3] hover:text-[#FF2D2D] hover:border-[#FF2D2D]"
                  >
                    <ArrowCounterClockwise size={13} />
                  </button>
                  <Link
                    to={`/admin/projects/${p.id}`}
                    data-testid={`archive-open-${p.id}`}
                    title="Open project"
                    className="p-2 text-[#a3a3a3] hover:text-white"
                  >
                    <ArrowUpRight size={14} />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
